import {
  MapContainer,
  TileLayer,
  GeoJSON,
  CircleMarker,
  Polygon,
  LayersControl,
  WMSTileLayer,
  Popup,
} from "react-leaflet";
import { FeatureCollection } from "geojson";
import L, { LatLngExpression } from "leaflet";
import { useEffect, useState } from "react";
import { SessionManager } from "../../utils/consts";
import { Box, Typography } from "@mui/material";

const propertyColors = {
  grass: "green",
  asphalt: "black",
  building: "red",
  concrete: "gray",
  edging: "darkgray",
  low_vegetation: "lightgreen",
  medium_high_vegetation: "darkgreen",
  muddy: "brown",
  natural_ground: "white",
  water: "blue",
};

interface MapProps {
  sessionManager: SessionManager;
  asphalt_geojson: FeatureCollection | null;
  building_geojson: FeatureCollection | null;
  concrete_geojson: FeatureCollection | null;
  edging_geojson: FeatureCollection | null;
  grass_geojson: FeatureCollection | null;
  low_vegetation_geojson: FeatureCollection | null;
  medium_high_vegetation_geojson: FeatureCollection | null;
  muddy_geojson: FeatureCollection | null;
  natural_ground_geojson: FeatureCollection | null;
  water_geojson: FeatureCollection | null;
}

export interface ProfileProps {
  email: string;
  id: number;
  name: string;
  phone_number: string;
  address: string;
  location: string;
}

const Map: React.FC<MapProps> = ({
  sessionManager,
  asphalt_geojson,
  building_geojson,
  concrete_geojson,
  edging_geojson,
  grass_geojson,
  low_vegetation_geojson,
  medium_high_vegetation_geojson,
  muddy_geojson,
  natural_ground_geojson,
  water_geojson,
}) => {
  const [profile, setProfile] = useState<ProfileProps | null>(null);
  const getProfile = async () => {
    const responseData = await sessionManager.getData("profile");
    setProfile(responseData);
  };

  useEffect(() => {
    getProfile();
  }, []);

  return (
    <div id="map" style={{ height: "100vh", width: "100vw" }}>
      <MapContainer
        center={[-33.427653808909376, 151.2977724234562]}
        zoom={17}
        // maxZoom={22}
        style={{ height: "100%", width: "100%" }}
      >
        <LayersControl position="topleft">
          <LayersControl.BaseLayer checked name="Satellite">
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              attribution="Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Map">
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
          </LayersControl.BaseLayer>
          {grass_geojson && (
            <GeoJSON
              data={grass_geojson}
              style={(feature) => ({
                color: feature?.properties?.stroke,
                weight: feature?.properties?.["stroke-width"],
                opacity: feature?.properties?.["stroke-opacity"],
                fillColor: feature?.properties?.fill,
                fillOpacity: feature?.properties?.["fill-opacity"],
              })}
              onEachFeature={(feature, layer) => {
                layer.on("click", () => {
                  const uniqueId = `popup-content-${feature.properties.id}`;
                  layer
                    .bindPopup(
                      `<div id="${uniqueId}" style="width: 150px;"></div>`
                    )
                    .openPopup();
                  const popupContent = document.getElementById(uniqueId);
                  if (popupContent) {
                    popupContent.innerHTML = `
                      <div style="text-align: center;">
                        <h3>Grass</h3>
                        <p>Area: ${feature.properties.area.toFixed(2)} m²</p>
                        <p>Slope: ${feature.properties.slope_lower}-${
                      feature.properties.slope_upper
                    }%</p>
                    <p>Color: ${feature.properties.color_hex}</p>
                      </div>
                    `;
                  }
                });
              }}
            />
          )}

          {asphalt_geojson && (
            <GeoJSON
              data={asphalt_geojson}
              style={{
                color: propertyColors.asphalt,
                fillColor: propertyColors.asphalt,
                fillOpacity: 0.8,
              }}
            />
          )}

          {building_geojson && (
            <GeoJSON
              data={building_geojson}
              style={{
                color: propertyColors.building,
                fillColor: propertyColors.building,
                fillOpacity: 0.8,
              }}
            />
          )}

          {concrete_geojson && (
            <GeoJSON
              data={concrete_geojson}
              style={{
                color: propertyColors.concrete,
                fillColor: propertyColors.concrete,
                fillOpacity: 0.8,
              }}
            />
          )}

          {edging_geojson && (
            <GeoJSON
              data={edging_geojson}
              style={{
                color: propertyColors.edging,
                fillColor: propertyColors.edging,
                fillOpacity: 0.8,
              }}
            />
          )}

          {low_vegetation_geojson && (
            <GeoJSON
              data={low_vegetation_geojson}
              style={{
                color: propertyColors.low_vegetation,
                fillColor: propertyColors.low_vegetation,
                fillOpacity: 0.8,
              }}
            />
          )}

          {medium_high_vegetation_geojson && (
            <GeoJSON
              data={medium_high_vegetation_geojson}
              style={{
                color: propertyColors.medium_high_vegetation,
                fillColor: propertyColors.medium_high_vegetation,
                fillOpacity: 0.8,
              }}
            />
          )}

          {muddy_geojson && (
            <GeoJSON
              data={muddy_geojson}
              style={{
                color: propertyColors.muddy,
                fillColor: propertyColors.muddy,
                fillOpacity: 0.8,
              }}
            />
          )}

          {natural_ground_geojson && (
            <GeoJSON
              data={natural_ground_geojson}
              style={{
                color: propertyColors.natural_ground,
                fillColor: propertyColors.natural_ground,
                fillOpacity: 0.8,
              }}
            />
          )}

          {water_geojson && (
            <GeoJSON
              data={water_geojson}
              style={{
                color: propertyColors.water,
                fillColor: propertyColors.water,
                fillOpacity: 0.8,
              }}
            />
          )}

          {profile && profile.location && (
            <CircleMarker
              center={
                profile.location.split(",").map(Number) as [number, number]
              }
              radius={5}
              color="purple"
              fillColor="purple"
              fillOpacity={0.8}
            />
          )}
        </LayersControl>
      </MapContainer>
    </div>
  );
};

export default Map;
